"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.microsoftAgentAdapter = void 0;
const helpers_1 = require("../helpers");
const zod_1 = require("zod");
/**
 * Detection schemas for Microsoft Agent Framework format
 *
 * Microsoft Agent Framework uses a ChatML-like format with parts arrays:
 * - Messages: {role: "user|assistant|tool", parts: [{type, ...}]}
 * - Tool calls: {type: "tool_call", id: [...], name, arguments}
 * - Tool responses: {type: "tool_call_response", id: [...], response}
 * - Text: {type: "text", content}
 */
// Microsoft Agent message with parts array
const MicrosoftAgentMessageSchema = zod_1.z.looseObject({
    role: zod_1.z.enum(["user", "assistant", "tool"]),
    parts: zod_1.z.array(zod_1.z.looseObject({
        type: zod_1.z.enum(["text", "tool_call", "tool_call_response"]),
    })),
});
// Array of Microsoft Agent messages
const MicrosoftAgentMessagesSchema = zod_1.z.array(MicrosoftAgentMessageSchema);
function isRecord(value) {
    return Boolean(value) && typeof value === "object";
}
function hasPydanticAiMessageMarkers(data) {
    const messages = (() => {
        if (Array.isArray(data)) {
            return data;
        }
        if (isRecord(data) && Array.isArray(data.messages)) {
            return data.messages;
        }
        if (isRecord(data)) {
            return [data];
        }
        return [];
    })();
    return messages.some((msg) => {
        if (!isRecord(msg))
            return false;
        if (!Array.isArray(msg.parts))
            return false;
        return msg.parts.some((part) => {
            if (!isRecord(part))
                return false;
            return (part.type === "thinking" ||
                part.type === "redacted_thinking" ||
                (part.type === "tool_call_response" &&
                    ("result" in part || msg.role === "user")));
        });
    });
}
/**
 * Extract tool calls and text content from parts array
 * Handles: text, tool_call, tool_call_response
 */
function extractFromParts(parts) {
    const toolCalls = [];
    const textParts = [];
    let toolCallId;
    for (const part of parts) {
        if (!part || typeof part !== "object")
            continue;
        const p = part;
        // Handle tool_call: {type: "tool_call", id: [...], name, arguments}
        if (p.type === "tool_call") {
            // uses array IDs: ["run_id", "call_id"], we just need the call id,
            let callId = "";
            if (Array.isArray(p.id)) {
                const callIdMatch = p.id.find((id) => typeof id === "string" && id.startsWith("call_"));
                callId = callIdMatch || p.id[p.id.length - 1] || "";
            }
            else if (typeof p.id === "string") {
                callId = p.id;
            }
            toolCalls.push({
                id: callId,
                name: p.name,
                arguments: typeof p.arguments === "string"
                    ? p.arguments
                    : JSON.stringify(p.arguments ?? {}),
                type: "function",
            });
            continue;
        }
        // Handle text: {type: "text", content}
        if (p.type === "text" && typeof p.content === "string") {
            textParts.push(p.content);
            continue;
        }
        // Handle tool_call_response: {type: "tool_call_response", id: [...], response}
        if (p.type === "tool_call_response") {
            // we just need the call_id, again
            if (Array.isArray(p.id)) {
                const callIdMatch = p.id.find((id) => typeof id === "string" && id.startsWith("call_"));
                toolCallId = callIdMatch || p.id[p.id.length - 1] || "";
            }
            else if (typeof p.id === "string") {
                toolCallId = p.id;
            }
            textParts.push((0, helpers_1.stringifyToolResultContent)(p.response));
        }
    }
    return {
        toolCalls,
        text: textParts.join(""),
        toolCallId,
    };
}
/**
 * Extract tool definitions from metadata.attributes["gen_ai.tool.definitions"]
 * Microsoft Agent Framework uses OpenAI-style tool definitions
 */
function extractToolDefinitions(tools) {
    if (!Array.isArray(tools))
        return [];
    const definitions = [];
    for (const tool of tools) {
        if (!tool || typeof tool !== "object")
            continue;
        const t = tool;
        // Handle OpenAI-style tool definitions: {type: "function", function: {name, description, parameters}}
        // intentionally not imported from openai for separation of concerns
        if (t.type === "function" && t.function && typeof t.function === "object") {
            const func = t.function;
            const toolDef = {
                name: func.name || "",
            };
            if (func.description !== null && func.description !== undefined) {
                toolDef.description = func.description;
            }
            if (func.parameters !== null && func.parameters !== undefined) {
                toolDef.parameters = func.parameters;
            }
            definitions.push(toolDef);
        }
    }
    return definitions;
}
// Normalize a single Microsoft Agent message to our ChatML format
function normalizeMicrosoftAgentMessage(msg) {
    if (!msg || typeof msg !== "object")
        return {};
    const message = msg;
    const normalized = { ...message };
    if (normalized.parts && Array.isArray(normalized.parts)) {
        const { toolCalls, text, toolCallId } = extractFromParts(normalized.parts);
        if (toolCalls.length > 0) {
            normalized.tool_calls = toolCalls;
        }
        if (text) {
            normalized.content = text;
        }
        // Set tool_call_id for tool messages (from tool_call_response)
        if (toolCallId && normalized.role === "tool") {
            normalized.tool_call_id = toolCallId;
        }
        // prevent showing in passthrough
        delete normalized.parts;
    }
    // For tool messages with rich object content, spread into message
    // so it goes to json passthrough field → renders as PrettyJsonView.
    // Rich = nested structure OR 3+ keys. Simple <=2 scalar keys.
    if (normalized.role === "tool" &&
        typeof normalized.content === "object" &&
        normalized.content !== null &&
        !Array.isArray(normalized.content)) {
        if ((0, helpers_1.isRichToolResult)(normalized.content)) {
            // Rich object: spread for table rendering
            const { content, ...rest } = normalized;
            return { ...rest, ...content };
        }
        // Simple object: stringify for text rendering
        normalized.content = (0, helpers_1.stringifyToolResultContent)(normalized.content);
    }
    return normalized;
}
function normalizeMessages(data) {
    return data.map(normalizeMicrosoftAgentMessage);
}
function preprocessData(data, ctx) {
    if (!data)
        return data;
    if (Array.isArray(data)) {
        const normalized = normalizeMessages(data);
        // extract tool definitions from metadata
        const meta = (0, helpers_1.parseMetadata)(ctx.metadata);
        const toolDefinitions = (0, helpers_1.getNestedProperty)(meta, "attributes", "gen_ai.tool.definitions");
        if (toolDefinitions) {
            const tools = extractToolDefinitions(toolDefinitions);
            if (tools.length > 0) {
                return (0, helpers_1.attachToolDefinitionsToMessages)(normalized, tools);
            }
        }
        return normalized;
    }
    // messages wrapper
    if (typeof data === "object" && "messages" in data) {
        const obj = data;
        const messages = Array.isArray(obj.messages)
            ? normalizeMessages(obj.messages)
            : obj.messages;
        const tools = (0, helpers_1.normalizeToolDefinitionsForChatMl)(obj.tools);
        if (Array.isArray(messages) && tools.length > 0) {
            return {
                ...obj,
                messages: (0, helpers_1.attachToolDefinitionsToMessages)(messages, tools),
            };
        }
        return {
            ...obj,
            messages,
        };
    }
    // single message
    if (typeof data === "object" && "role" in data) {
        return normalizeMicrosoftAgentMessage(data);
    }
    return data;
}
exports.microsoftAgentAdapter = {
    id: "microsoft-agent",
    detect(ctx) {
        const meta = (0, helpers_1.parseMetadata)(ctx.metadata);
        // HINTS: Fast checks for explicit Microsoft Agent Framework indicators
        if (ctx.framework === "microsoft-agent")
            return true;
        const scopeName = (0, helpers_1.getNestedProperty)(meta, "scope", "name");
        if (scopeName === "agent_framework")
            return true;
        if (typeof scopeName === "string" &&
            scopeName.includes("Microsoft.Extensions.AI"))
            return true;
        if (scopeName === "pydantic-ai")
            return false;
        const providerName = (0, helpers_1.getNestedProperty)(meta, "attributes", "gen_ai.provider.name");
        if (providerName === "microsoft.agent_framework")
            return true;
        if (hasPydanticAiMessageMarkers(ctx.metadata) ||
            hasPydanticAiMessageMarkers(ctx.data)) {
            return false;
        }
        // STRUCTURAL: Schema-based detection on metadata
        if (MicrosoftAgentMessagesSchema.safeParse(ctx.metadata).success)
            return true;
        // Schema-based detection on data (slower, do last)
        if (MicrosoftAgentMessagesSchema.safeParse(ctx.data).success)
            return true;
        if (MicrosoftAgentMessageSchema.safeParse(ctx.data).success)
            return true;
        return false;
    },
    preprocess(data, _kind, ctx) {
        return preprocessData(data, ctx);
    },
};
